import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Table, TableContainer, ThTable, ThTableItem } from './SmrtSearchTableNew'
import { useLexicon } from '../../hooks/useLexicon'
import { extractFirstCharacters } from '../../helpers';
import { formatMatchScore } from '../../helpers/matchScore';
import { swapInTeamLogoPlaceholder, teamLogoPlaceholder } from '../../helpers/teamLogo';
import { openStatVideo } from '../../helpers/service';
import { useParameterLexicon } from '../../hooks/useParameterLexicon';
import Button from "../button/index";
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { VariableSizeList } from 'react-window';

import { ReactComponent as SortAscIcon } from "../../images/icons/sortasc_icon.svg";
import { ReactComponent as SortDescIcon } from "../../images/icons/sort_desc.svg";
import { ReactComponent as SortDefaultIcon } from "../../images/icons/sort_default.svg";
import more from "../../images/ic_more.svg";
import play from "../../images/ic_play.svg";
import playMatchBlue from "../../images/play_match_blue.svg";
import playMatchDarkActive from "../../images/ic_play_active.svg";
import playMatchBlueActive from "../../images/ic_play_active_blue.svg";
import emptySearch from "../../images/ic_empty.svg";
import playInBall from "../../images/ic_playinball.svg";
import playInBallBlue from "../../images/playInBall_blue.svg";
import statistics from "../../images/ic_statistics.svg";
import statisticsBlue from "../../images/statistics_blue.svg";
import formation from "../../images/ic_formation.svg";
import formationBlue from "../../images/formation_blue.svg";
import metrica from "../../images/metrica-logo.png";
import downloadIcon from "../../images/ic_download.svg";
import downloadIconBlue from "../../images/download_blue.svg";

import { Action, MatchContent, MatchInfo, Score, TeamName, TeamScore } from '../../style/MatchTable';
import Moment from 'react-moment';
import { useNavigate } from 'react-router-dom';
import { Formation } from '../Formation';
import useOutsideClick from '../../hooks/useOutsideClick';
import { createPortal } from 'react-dom';
import { EmptyState } from '../../style/Tournaments';
import { Oval } from 'react-loader-spinner';
import {$apiV2} from "../../config/api";
import {useNotification} from "../notification/useNotification";
import { NotificationList } from "../notification/NotificationList";
import styled from "styled-components";

const _isLightTheme = localStorage.getItem('theme') === 'light';

const PopupOverlay = styled.div`
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.65);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
    z-index: 9999;
`;

const PopupContent = styled.div`
    background: #000000;
    border-radius: 12px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.25);
    padding: 32px 40px;
    max-width: 520px;
    width: 100%;
    text-align: center;
`;

const PopupMessage = styled.p`
    color: ${(props) => props.theme.white};
    font-family: "SatoshiMedium";
    font-size: 18px;
    line-height: 26px;
    margin-bottom: 24px;
`;

const PopupActions = styled.div`
    display: flex;
    justify-content: center;
`;

const TopHorizontalScroll = styled.div`
    width: 100%;
    overflow-x: auto;
    overflow-y: hidden;
    margin-bottom: 8px;
    height: 14px;
`;

const TopHorizontalScrollInner = styled.div`
    height: 1px;
`;

const TopOnlyTableContainer = styled(TableContainer)`
    width: 100%;
    max-width: 100%;
    overflow-x: auto;
    overflow-y: auto;

    &::-webkit-scrollbar:horizontal {
        display: none;
        height: 0;
    }
`;

const StickyTableHeader = styled(ThTable)`
    th {
        position: sticky;
        top: 0;
        z-index: 6;
        box-sizing: border-box;
        background: ${(props) => props.theme.black};
    }

    th.sticky {
        z-index: 7;
    }
`;

const VIRTUAL_MATCH_ROW_HEIGHT = 64;
const VIRTUAL_SUBHEADER_ROW_HEIGHT = 34;

const VirtualBody = styled.div`
    position: relative;
    width: max-content;
    min-width: 100%;

    .ReactVirtualized__Grid,
    [style*="overflow: auto"] {
        scrollbar-width: auto;
    }
`;

const VirtualRow = styled.div`
    display: flex;
    align-items: stretch;
    border-bottom: 1px solid ${(props) => (props.$colored ? '#272727' : props.theme.black)};
    background: ${(props) =>
        props.$colored ? props.theme.row : 'transparent'};

    &::after {
        content: '';
        position: absolute;
        inset: 0;
        z-index: 2;
        opacity: ${(props) => (props.$highlighted && !props.$highlightFading ? 1 : 0)};
        background: rgba(191, 232, 45, 0.14);
        box-shadow: inset 4px 0 0 ${(props) => props.theme.green}, inset 0 0 0 1px rgba(191, 232, 45, 0.36);
        pointer-events: none;
        transition: opacity 520ms ${(props) =>
            props.$highlightFading ? 'cubic-bezier(0.55, 0, 1, 0.45)' : 'cubic-bezier(0.16, 1, 0.3, 1)'};
    }

    .virtual-cell {
        min-width: 100px;
        width: 100px;
        display: flex;
        align-items: center;
        justify-content: center;
        color: ${(props) => props.theme.white};
        font-family: "SatoshiMedium";
        font-size: 16px;
        font-weight: 500;
        box-shadow: inset 0 -1px 0 ${(props) => (props.$colored ? '#272727' : props.theme.black)};
    }

    .virtual-cell.sticky {
        position: sticky;
        background: ${(props) =>
            props.$highlighted && !props.$highlightFading
                ? 'rgb(43, 59, 6)'
                : props.$colored
                    ? props.theme.row
                    : props.theme.black} !important;
        opacity: 1;
        z-index: 1;
        transition: background-color 520ms ${(props) =>
            props.$highlightFading ? 'cubic-bezier(0.55, 0, 1, 0.45)' : 'cubic-bezier(0.16, 1, 0.3, 1)'};
    }

    .virtual-cell.parameter-search-highlight {
        background: ${(props) =>
            props.$parameterHighlightFading ? 'transparent' : 'rgba(191, 232, 45, 0.14)'};
        box-shadow: ${(props) =>
            props.$parameterHighlightFading ? 'none' : 'inset 0 2px 0 rgba(191, 232, 45, 0.58)'};
        transition: background-color 520ms ${(props) =>
                props.$parameterHighlightFading ? 'cubic-bezier(0.55, 0, 1, 0.45)' : 'cubic-bezier(0.16, 1, 0.3, 1)'},
            box-shadow 520ms ${(props) =>
                props.$parameterHighlightFading ? 'cubic-bezier(0.55, 0, 1, 0.45)' : 'cubic-bezier(0.16, 1, 0.3, 1)'};
    }

    .virtual-cell.name {
        position: relative !important;
        left: auto !important;
        min-width: 356px;
        width: 356px;
        justify-content: flex-start;
        transform: translate3d(var(--team-matches-scroll-left, 0px), 0, 0);
        will-change: transform;
    }

    .virtual-cell.season {
        left: 356px;
    }
`;

const ParameterHeader = styled(ThTableItem)`
    background: ${(props) =>
        props.$highlighted && !props.$fading ? 'rgba(191, 232, 45, 0.14)' : 'transparent'};
    box-shadow: ${(props) =>
        props.$highlighted && !props.$fading ? 'inset 0 3px 0 rgba(191, 232, 45, 0.78)' : 'none'};
    transition: background-color 520ms ${(props) =>
            props.$fading ? 'cubic-bezier(0.55, 0, 1, 0.45)' : 'cubic-bezier(0.16, 1, 0.3, 1)'},
        box-shadow 520ms ${(props) =>
            props.$fading ? 'cubic-bezier(0.55, 0, 1, 0.45)' : 'cubic-bezier(0.16, 1, 0.3, 1)'};
`;

const VirtualSubheader = styled.div`
    display: flex;
    align-items: center;
    color: ${() => (_isLightTheme ? '#2363F7' : '#BFE82D')};
    font-family: "SatoshiMedium";
    font-size: 13px;
    font-weight: 500;
    border-bottom: 1px solid ${(props) => props.theme.black};

    .league-label {
        display: flex;
        align-items: center;
        width: 456px;
        height: 100%;
        padding: 0 20px;
        background: ${(props) => props.theme.black};
        color: ${() => (_isLightTheme ? '#2363F7' : '#BFE82D')};
        transform: translate3d(var(--team-matches-scroll-left, 0px), 0, 0);
        will-change: transform;
        z-index: 4;
    }
`;

const TeamMatchItemMenu = ({ item, currentTeam, tableRef }) => {
    const l = useLexicon();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [openRow, setOpenRow] = useState(false);
    const [openFormation, setOpenFormation] = useState(false);
    const [selectedFormationMatch, setSelectedFormationMatch] = useState(null)
    const [position, setPosition] = useState("bottom");
    const [isMetricaPopupOpen, setIsMetricaPopupOpen] = useState(false);
    const [metricaPopupMessage, setMetricaPopupMessage] = useState("");
    const [downloadingReportMatchId, setDownloadingReportMatchId] = useState(null);

    const canDownloadReport = useSelector((state) => state.user.canDownloadReport);

    const { notifications, addNotification, removeNotification } =
        useNotification();

    const [menuCoords, setMenuCoords] = useState(null);

    useEffect(() => {
        if (openRow && menuDropdownRef.current && tableRef.current) {
            const menuRect = menuDropdownRef.current.getBoundingClientRect();
            const scrollableParent = tableRef.current || document.documentElement;
            const parentRect = scrollableParent.getBoundingClientRect();

            const newPosition = menuRect.bottom + 250 > parentRect.bottom ? "top" : "bottom";
            setPosition(newPosition);
            setMenuCoords({ top: menuRect.top, left: menuRect.left });
        }
    }, [openRow]);

    useEffect(() => {
        if (openRow && menuDropdownRef.current && tableRef.current) {
            const menuRect = menuDropdownRef.current.getBoundingClientRect();
            const tableRect = tableRef.current.getBoundingClientRect();

            console.log(menuRect, tableRect, menuRect.bottom > tableRect.bottom)

            if (menuRect.bottom + 250 > tableRect.bottom) {
                setPosition("top");
            } else {
                setPosition("bottom");
            }
        }
    }, [openRow]);

    const menuDropdownRef = useOutsideClick(() => {
        if (openRow) {
            setOpenRow(false);
        }
    });

    const handleOpenFormation = (match) => {
        setSelectedFormationMatch(match);
        setOpenFormation(true);
    };

    const handleCloseFormation = () => {
        setOpenFormation(false);
        setSelectedFormationMatch(null);
    };

    const getColorScore = (item) => {
        const isTeamHome = item.home_team === currentTeam;
        if (isTeamHome) {
            return item.home_team_score > item.away_team_score
                ? "#36E82D"
                : item.home_team_score < item.away_team_score
                    ? "#F20A46"
                    : "#E8D02D";
        } else {
            return item.home_team_score > item.away_team_score
                ? "#F20A46"
                : item.home_team_score < item.away_team_score
                    ? "#36E82D"
                    : "#E8D02D";
        }
    };

    const onBallEventsClick = (match, new_tab) => {
        const obe = (match?.parameters || []).find(
            (param) =>
                param.parameter_name === "On-Ball Events" || param.parameter_id === 13
        );
        if (!obe || !(Number(obe.value) > 0)) {
            return;
        }
        // markers are no longer inlined per match; fetch this match's markers on click.
        const teamId =
            match?.away_team === currentTeam ? match?.away_team_id : match?.home_team_id;
        openStatVideo(
            { kind: "team", id: teamId, matchIds: [match?.id], paramId: obe.parameter_id ?? 13 },
            `On Ball Events - ${match?.home_team} ${match?.away_team}`,
            !!new_tab
        );
    };


    const handleCloseMetricaPopup = () => {
        setIsMetricaPopupOpen(false);
        setMetricaPopupMessage("");
    };

    const handleDownloadReportOption = async (match) => {
        const matchId = Number(match?.id);
        if (!Number.isFinite(matchId) || downloadingReportMatchId !== null) return;

        const notifId = `dl-match-report-${matchId}`;
        setOpenRow(false);
        setDownloadingReportMatchId(matchId);
        addNotification("Download in progress...", "info", { id: notifId, duration: 0 });

        try {
            const response = await $apiV2.post(
                `/platform/match/${matchId}/export/report/pdf`,
                {},
                { responseType: "blob" }
            );
            const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
            const link = document.createElement("a");
            link.href = url;
            link.download = `match-report-${matchId}.pdf`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Match report download failed", error);
            addNotification("Error, please try again later.", "error");
        } finally {
            removeNotification(notifId);
            setDownloadingReportMatchId((prev) => Number(prev) === matchId ? null : prev);
        }
    };

    const handleMetrica = async (match) => {
        const hasVideo = Boolean(match?.video || match?.videos?.some((video) => Boolean(video?.link)));
        if (!hasVideo) {
            return;
        }
        try {
            await $apiV2.get(
                `/metrica/${match.id}`,
            );

            setMetricaPopupMessage(
                "Metrica Nexus Container download started, you will receive the container via email."
            );
            setIsMetricaPopupOpen(true);
        } catch (err) {
            addNotification('Error, please try again later.', 'error');
        }
    }

    const hasVideo = Boolean(item?.video || item?.videos?.some((video) => Boolean(video?.link)));

    return (
        <>
            <TeamScore style={{ position: "relative" }} ref={menuDropdownRef}>
                <span
                    onClick={() => window.open(`/tournaments/statistics/${item.id}`)}
                    style={{
                        color: item?.hasStats ? getColorScore(item) : '#fff',
                        cursor: 'pointer'
                    }}
                >
                    {item?.hasStats ? formatMatchScore(item) : "-:-"}
                </span>
                <img
                    style={{ right: 'auto' }}
                    className="more"
                    src={more}
                    alt={more}
                    onClick={() => setOpenRow(prev => !prev)}

                />
                {openRow && menuCoords && createPortal(
                    <MatchInfo
                        style={{
                            // top: position === "top" ? "auto" : "100%", 
                            // bottom: position === "top" ? "100%" : "auto",
                            top: position === "top" ? `${menuCoords?.top - 210}px` : `${menuCoords?.top + 30}px`,
                            left: `${menuCoords?.left}px`,
                            zIndex: 9999
                        }}
                    >
                        <MatchContent>
                            <Action
                                style={{
                                    cursor: hasVideo ? 'pointer' : 'not-allowed',
                                    opacity: hasVideo ? '1' : '0.5',
                                }}
                                onClick={() => {
                                    if (hasVideo) {
                                        window.open(
                                            `/tournaments/match/video/${item.id}`,
                                            "_blank"
                                        );
                                    }
                                }}
                            // onContextMenu={() =>
                            //   window.open(
                            //     `/tournaments/match/video/${item.id}`,
                            //     "_blank"
                            //   )
                            // }
                            >
                                <img
                                    src={
                                        localStorage.getItem("theme") === "light"
                                            ? hasVideo
                                                ? playMatchBlueActive
                                                : playMatchBlue
                                            : hasVideo
                                                ? playMatchDarkActive
                                                : play
                                    }
                                    alt={play}
                                />
                                <h4
                                    className={
                                        hasVideo ? "haveMatch" : ""
                                    }
                                >
                                    {hasVideo ? l(1019) : t("matchActions.notAvailable")}
                                </h4>
                            </Action>
                            <Action
                                onClick={() => onBallEventsClick(item)}
                                onContextMenu={() =>
                                    onBallEventsClick(item, true)
                                }
                            >
                                <img
                                    src={
                                        localStorage.getItem("theme") === "light"
                                            ? playInBallBlue
                                            : playInBall
                                    }
                                    alt={playInBall}
                                />
                                <h4>{l(334)}</h4>
                            </Action>
                            <Action
                                title={hasVideo ? l(1126) : l(316)}
                                style={{
                                    cursor: hasVideo ? 'pointer' : 'not-allowed',
                                    opacity: hasVideo ? '1' : '0.5',
                                }}
                                onClick={() => {
                                    if (!hasVideo) {
                                        return;
                                    }
                                    handleMetrica(item);
                                }}
                                onContextMenu={(e) => {
                                    if (!hasVideo) {
                                        e.preventDefault();
                                        return;
                                    }
                                    handleMetrica(item);
                                }}
                                >
                                <img style={{width: '20px', height: '20px'}} src={metrica} alt={metrica}></img>
                                <h4>{l(1125)}</h4>

                            </Action>
                            <Action
                                onClick={() =>
                                    navigate(`/tournaments/statistics/${item.id}`)
                                }
                                onContextMenu={() =>
                                    window.open(
                                        `/tournaments/statistics/${item.id}`,
                                        "_blank"
                                    )
                                }
                            >
                                <img
                                    src={
                                        localStorage.getItem("theme") === "light"
                                            ? statisticsBlue
                                            : statistics
                                    }
                                    alt={statistics}
                                />
                                <h4>{l(1020)}</h4>
                            </Action>
                            <Action
                                onClick={() => handleOpenFormation(item)}
                                onContextMenu={() => handleOpenFormation(item)}
                            >
                                <img
                                    src={
                                        localStorage.getItem("theme") === "light"
                                            ? formationBlue
                                            : formation
                                    }
                                    alt={formation}
                                />
                                <h4>{l(1021)}</h4>
                            </Action>
                            {canDownloadReport === true && (
                                <Action
                                    style={{
                                        cursor: downloadingReportMatchId !== null ? "not-allowed" : "pointer",
                                        opacity: downloadingReportMatchId !== null ? 0.5 : 1,
                                    }}
                                    onClick={() => {
                                        if (downloadingReportMatchId !== null) return;
                                        handleDownloadReportOption(item);
                                    }}
                                >
                                    <img
                                        src={
                                            localStorage.getItem("theme") === "light"
                                                ? downloadIconBlue
                                                : downloadIcon
                                        }
                                        alt="download"
                                    />
                                    <h4>
                                        {Number(downloadingReportMatchId) === Number(item?.id)
                                            ? l(312)
                                            : t("statistics.downloadReport")}
                                    </h4>
                                </Action>
                            )}
                        </MatchContent>
                    </MatchInfo>,
                    document.body
                )}
            </TeamScore>
            {/* display formation */}
            <Formation
                open={openFormation}
                handleClose={(handleCloseFormation)}
                match={selectedFormationMatch}
            />

            {isMetricaPopupOpen && createPortal(
                <PopupOverlay onClick={handleCloseMetricaPopup}>
                    <PopupContent onClick={(e) => e.stopPropagation()}>
                        <PopupMessage>{metricaPopupMessage}</PopupMessage>
                        <PopupActions>
                        <Button
                  title="Close"
                  onClick={handleCloseMetricaPopup}
                  size={"full"}
                />
                        </PopupActions>
                    </PopupContent>
                </PopupOverlay>,
                document.body
            )}
            <NotificationList notifications={notifications} onClose={removeNotification} />
        </>
    )
}

export const TeamMatchesTable = ({ data, currentTeam, loading, handleSortTable, tableTypeSorting, tempSelectedFilters, opponentSearchTerm = '' }) => {
    const l = useLexicon();
    const { getTeamParameterLexiconId } = useParameterLexicon();
    const tableRef = useRef(null);
    const topScrollRef = useRef(null);
    const listRef = useRef(null);
    const listOuterRef = useRef(null);
    const searchHighlightTimeoutRef = useRef(null);
    const searchHighlightClearTimeoutRef = useRef(null);
    const parameterHighlightTimeoutRef = useRef(null);
    const parameterHighlightClearTimeoutRef = useRef(null);
    const syncScrollLockRef = useRef(false);
    const [topScrollWidth, setTopScrollWidth] = useState(0);
    const [isTopScrollVisible, setIsTopScrollVisible] = useState(false);
    const [highlightedMatch, setHighlightedMatch] = useState(null);
    const [highlightedParameter, setHighlightedParameter] = useState(null);
    const [viewportHeight, setViewportHeight] = useState(() =>
        typeof window === 'undefined' ? 720 : window.innerHeight
    );

    const tableHeadNames = useMemo(() => {
        if (data?.length > 0) {
            const dataWithParams = data.find(item => item?.parameters?.length > 0);
            if (dataWithParams) {
                return dataWithParams.parameters;
            }
            return tempSelectedFilters?.params || []
        }
        return tempSelectedFilters?.params || [];
    }, [data, tempSelectedFilters]);

    const getParameterDisplayName = (parameter) => {
        const fallbackName =
            typeof parameter === 'string'
                ? parameter
                : parameter?.parameter_name || parameter?.name || '';
        if (!fallbackName) return '';
        const lexiconId = getTeamParameterLexiconId(parameter);
        if (lexiconId) {
            return l(lexiconId) || fallbackName;
        }
        return fallbackName;
    };

    const isCanClickStat = (parameter) => {
        // markers are lazy-loaded on click now, so gate on the value alone.
        return (
            parameter?.value === 0 ||
            parameter.parameter_name.includes("%") ||
            parameter.parameter_name.includes("Mins Played")
        );
    };

    const getCompetitionName = (row) => {
        const c = row?.competition;
        if (typeof c === 'string') return c;
        if (c != null && typeof c === 'object') return c?.name ?? c?.title ?? null;
        return row?.competition__name ?? row?.competition_name ?? null;
    };

    const getRoundName = (row) => {
        const val = row?.round_name ?? row?.round ?? row?.matchday ?? row?.match_round ?? null;
        if (val != null && typeof val === 'object') return val?.name ?? val?.title ?? null;
        return val;
    };

    const getCompetitionRoundLabel = (row) => {
        const comp = getCompetitionName(row);
        const round = getRoundName(row);
        if (!comp && !round) return null;
        if (comp && round) return `${comp} - ${round}`;
        return comp || round;
    };

    const colSpanCount = 2 + (tableHeadNames?.length || 0);
    const virtualTableWidth = 456 + (tableHeadNames?.length || 0) * 100;

    const virtualRows = useMemo(() => {
        if (!Array.isArray(data) || data.length === 0) {
            return [];
        }

        return data.flatMap((row, index) => {
            const label = getCompetitionRoundLabel(row);
            const prevLabel = index > 0 ? getCompetitionRoundLabel(data[index - 1]) : null;
            const rows = [];

            if (label && label !== prevLabel) {
                rows.push({
                    id: `subheader-${label}-${row?.id ?? index}`,
                    type: 'subheader',
                    label,
                });
            }

            rows.push({
                id: row?.id ?? `match-${index}`,
                type: 'match',
                row,
                dataIndex: index,
            });

            return rows;
        });
    }, [data]);

    const virtualListHeight = useMemo(() => {
        const fullHeight = virtualRows.reduce(
            (sum, item) =>
                sum + (item.type === 'subheader' ? VIRTUAL_SUBHEADER_ROW_HEIGHT : VIRTUAL_MATCH_ROW_HEIGHT),
            0
        );
        const maxHeight = Math.max(320, Math.round(viewportHeight * 0.58));

        return Math.max(160, Math.min(fullHeight || 160, maxHeight));
    }, [virtualRows, viewportHeight]);

    useEffect(() => {
        const searchTerm = opponentSearchTerm.trim().toLocaleLowerCase();

        if (!searchTerm) {
            window.clearTimeout(searchHighlightTimeoutRef.current);
            window.clearTimeout(searchHighlightClearTimeoutRef.current);
            window.clearTimeout(parameterHighlightTimeoutRef.current);
            window.clearTimeout(parameterHighlightClearTimeoutRef.current);
            setHighlightedMatch(null);
            setHighlightedParameter(null);
            return undefined;
        }

        const foundRow = data?.find((row) => {
            const opponent = row?.away_team === currentTeam ? row?.home_team : row?.away_team;
            return String(opponent || '').toLocaleLowerCase().includes(searchTerm);
        });

        const foundParameterIndex = tableHeadNames.findIndex((parameter) =>
            String(parameter?.parameter_name || parameter?.name || '')
                .toLocaleLowerCase()
                .includes(searchTerm)
        );

        if (!foundRow && foundParameterIndex < 0) {
            window.clearTimeout(searchHighlightTimeoutRef.current);
            window.clearTimeout(searchHighlightClearTimeoutRef.current);
            window.clearTimeout(parameterHighlightTimeoutRef.current);
            window.clearTimeout(parameterHighlightClearTimeoutRef.current);
            setHighlightedMatch(null);
            setHighlightedParameter(null);
            return undefined;
        }

        if (foundRow) {
            const virtualIndex = virtualRows.findIndex(
                (item) => item.type === 'match' && String(item.row?.id) === String(foundRow.id)
            );

            if (virtualIndex >= 0) {
                const itemOffset = virtualRows.slice(0, virtualIndex).reduce(
                    (offset, item) =>
                        offset + (item.type === 'subheader' ? VIRTUAL_SUBHEADER_ROW_HEIGHT : VIRTUAL_MATCH_ROW_HEIGHT),
                    0
                );
                const targetOffset = Math.max(0, itemOffset - virtualListHeight / 2 + VIRTUAL_MATCH_ROW_HEIGHT / 2);

                if (listOuterRef.current?.scrollTo) {
                    listOuterRef.current.scrollTo({ top: targetOffset, behavior: 'smooth' });
                } else {
                    listRef.current?.scrollToItem(virtualIndex, 'center');
                }
            }

            setHighlightedMatch({ id: foundRow.id, isFading: false });
            window.clearTimeout(searchHighlightTimeoutRef.current);
            window.clearTimeout(searchHighlightClearTimeoutRef.current);
            searchHighlightTimeoutRef.current = window.setTimeout(() => {
                setHighlightedMatch((currentHighlight) =>
                    String(currentHighlight?.id) === String(foundRow.id)
                        ? { ...currentHighlight, isFading: true }
                        : currentHighlight
                );
                searchHighlightClearTimeoutRef.current = window.setTimeout(() => {
                    setHighlightedMatch(null);
                }, 520);
            }, 5000);
        } else {
            window.clearTimeout(searchHighlightTimeoutRef.current);
            window.clearTimeout(searchHighlightClearTimeoutRef.current);
            setHighlightedMatch(null);
        }

        if (foundParameterIndex >= 0) {
            const parameterOffset = 456 + foundParameterIndex * 100;
            const horizontalTarget = Math.max(
                0,
                parameterOffset - (tableRef.current?.clientWidth || 0) / 2 + 50
            );
            tableRef.current?.scrollTo({ left: horizontalTarget, behavior: 'smooth' });

            setHighlightedParameter({ index: foundParameterIndex, isFading: false });
            window.clearTimeout(parameterHighlightTimeoutRef.current);
            window.clearTimeout(parameterHighlightClearTimeoutRef.current);
            parameterHighlightTimeoutRef.current = window.setTimeout(() => {
                setHighlightedParameter((currentHighlight) =>
                    currentHighlight?.index === foundParameterIndex
                        ? { ...currentHighlight, isFading: true }
                        : currentHighlight
                );
                parameterHighlightClearTimeoutRef.current = window.setTimeout(() => {
                    setHighlightedParameter(null);
                }, 520);
            }, 5000);
        } else {
            window.clearTimeout(parameterHighlightTimeoutRef.current);
            window.clearTimeout(parameterHighlightClearTimeoutRef.current);
            setHighlightedParameter(null);
        }

        return undefined;
    }, [currentTeam, data, opponentSearchTerm, tableHeadNames, virtualListHeight, virtualRows]);

    useEffect(() => () => {
        window.clearTimeout(searchHighlightTimeoutRef.current);
        window.clearTimeout(searchHighlightClearTimeoutRef.current);
        window.clearTimeout(parameterHighlightTimeoutRef.current);
        window.clearTimeout(parameterHighlightClearTimeoutRef.current);
    }, []);

    useEffect(() => {
        listRef.current?.resetAfterIndex?.(0, true);
    }, [virtualRows, tableHeadNames?.length]);

    useEffect(() => {
        const updateViewportHeight = () => {
            setViewportHeight(window.innerHeight);
        };

        window.addEventListener('resize', updateViewportHeight);

        return () => {
            window.removeEventListener('resize', updateViewportHeight);
        };
    }, []);

    const updateTopScrollbarState = useCallback(() => {
        const tableNode = tableRef.current;
        const topNode = topScrollRef.current;

        if (!tableNode) return;

        const hasHorizontalOverflow = tableNode.scrollWidth > tableNode.clientWidth + 1;
        setIsTopScrollVisible(hasHorizontalOverflow);
        setTopScrollWidth(tableNode.scrollWidth);

        if (topNode) {
            topNode.scrollLeft = tableNode.scrollLeft;
        }
    }, []);

    const updateStickyColumnOffset = useCallback((scrollLeft) => {
        tableRef.current?.style.setProperty('--team-matches-scroll-left', `${scrollLeft}px`);
    }, []);

    useEffect(() => {
        updateTopScrollbarState();
        const rafId = window.requestAnimationFrame(updateTopScrollbarState);
        window.addEventListener('resize', updateTopScrollbarState);
        window.addEventListener('navigationOpenChange', updateTopScrollbarState);

        let resizeObserver;
        if (typeof ResizeObserver !== 'undefined' && tableRef.current) {
            resizeObserver = new ResizeObserver(() => {
                updateTopScrollbarState();
            });
            resizeObserver.observe(tableRef.current);
        }

        return () => {
            window.cancelAnimationFrame(rafId);
            window.removeEventListener('resize', updateTopScrollbarState);
            window.removeEventListener('navigationOpenChange', updateTopScrollbarState);
            resizeObserver?.disconnect();
        };
    }, [updateTopScrollbarState, data, loading, tableHeadNames?.length]);

    useEffect(() => {
        const tableNode = tableRef.current;
        const topNode = topScrollRef.current;

        if (!tableNode || !topNode) return;

        const syncFromTop = () => {
            if (syncScrollLockRef.current) return;
            syncScrollLockRef.current = true;
            tableNode.scrollLeft = topNode.scrollLeft;
            updateStickyColumnOffset(topNode.scrollLeft);
            window.requestAnimationFrame(() => {
                syncScrollLockRef.current = false;
            });
        };

        const syncFromBottom = () => {
            if (syncScrollLockRef.current) return;
            syncScrollLockRef.current = true;
            topNode.scrollLeft = tableNode.scrollLeft;
            updateStickyColumnOffset(tableNode.scrollLeft);
            window.requestAnimationFrame(() => {
                syncScrollLockRef.current = false;
            });
        };

        topNode.addEventListener('scroll', syncFromTop, { passive: true });
        tableNode.addEventListener('scroll', syncFromBottom, { passive: true });
        updateStickyColumnOffset(tableNode.scrollLeft);

        return () => {
            topNode.removeEventListener('scroll', syncFromTop);
            tableNode.removeEventListener('scroll', syncFromBottom);
        };
    }, [isTopScrollVisible, topScrollWidth, updateStickyColumnOffset]);

    const renderVirtualRow = useCallback(({ index, style }) => {
        const item = virtualRows[index];
        if (!item) {
            return null;
        }

        if (item.type === 'subheader') {
            return (
                <VirtualSubheader style={{ ...style, width: virtualTableWidth }}>
                    <span className='league-label'>{item.label}</span>
                </VirtualSubheader>
            );
        }

        const {
            parameters,
            id,
            away_team,
            home_team,
            home_team_logo,
            away_team_logo,
            date,
            home_team_id,
            away_team_id,
        } = item.row;
        const isShouldColor = item.dataIndex % 2 === 0;

        return (
            <VirtualRow
                style={{ ...style, width: virtualTableWidth }}
                $colored={isShouldColor}
                $highlighted={String(item.row?.id) === String(highlightedMatch?.id)}
                $highlightFading={Boolean(highlightedMatch?.isFading)}
                $parameterHighlightFading={Boolean(highlightedParameter?.isFading)}
            >
                <div
                    className='virtual-cell sticky name'
                    style={{ zIndex: 4 }}
                >
                    <TeamName style={{ padding: '10px 20px', textAlign: 'left' }}>
                        {away_team === currentTeam ? (
                            <img
                                style={{
                                    width: "20px",
                                    height: "20px",
                                    objectFit: "contain",
                                }}
                                src={home_team_logo || teamLogoPlaceholder}
                                alt={home_team ?? ""}
                                onError={swapInTeamLogoPlaceholder}
                            />
                        ) : (
                            ""
                        )}

                        {home_team === currentTeam ? (
                            <img
                                style={{
                                    width: "20px",
                                    height: "20px",
                                    objectFit: "contain",
                                }}
                                src={away_team_logo || teamLogoPlaceholder}
                                alt={away_team ?? ""}
                                onError={swapInTeamLogoPlaceholder}
                            />
                        ) : (
                            ""
                        )}

                        <Score>
                            <h3>
                                <Moment format="DD.MM.YYYY">{date}</Moment>
                            </h3>
                            <h4
                                title={away_team === currentTeam
                                    ? home_team
                                    : away_team}
                                style={{ cursor: 'pointer' }}
                                onClick={() => window.open(`/tournaments/team/${away_team === currentTeam ? home_team_id : away_team_id}`)}
                            >
                                {away_team === currentTeam
                                    ? home_team
                                    : away_team}
                            </h4>
                        </Score>
                    </TeamName>
                </div>
                <div className='virtual-cell sticky season'>
                    <TeamMatchItemMenu item={item.row} currentTeam={currentTeam} tableRef={tableRef} />
                </div>
                {parameters?.length > 0 ? parameters?.map((param, idx) => {
                    const isCanClickParam = isCanClickStat(param);
                    return (
                        <div
                            className={`virtual-cell values${idx === highlightedParameter?.index ? ' parameter-search-highlight' : ''}`}
                            key={`${param.parameter_id}-${idx}`}
                            style={
                                isCanClickParam
                                    ? { cursor: "not-allowed" }
                                    : { cursor: "pointer" }
                            }
                            onClick={() =>
                                !isCanClickParam &&
                                openStatVideo({ kind: "team", id: away_team === currentTeam ? away_team_id : home_team_id, matchIds: [id], paramId: param.parameter_id }, `On Ball Events - ${home_team} ${away_team}`)
                            }
                            onContextMenu={(e) => {
                                e.preventDefault()
                                !isCanClickParam &&
                                openStatVideo({ kind: "team", id: away_team === currentTeam ? away_team_id : home_team_id, matchIds: [id], paramId: param.parameter_id }, `On Ball Events - ${home_team} ${away_team}`)
                            }}
                        >
                            {
                                Number.isInteger(param.value)
                                    ? param?.value?.toLocaleString()
                                    : param?.value?.toFixed(1)
                            }
                        </div>
                    )
                }) : <div className='virtual-cell values' style={{ width: `${tableHeadNames?.length * 100}px`, minWidth: `${tableHeadNames?.length * 100}px` }} />}
            </VirtualRow>
        );
    }, [currentTeam, highlightedMatch, highlightedParameter, tableHeadNames?.length, virtualRows, virtualTableWidth]);

    return (
        <>
            {isTopScrollVisible && (
                <TopHorizontalScroll ref={topScrollRef}>
                    <TopHorizontalScrollInner style={{ width: `${topScrollWidth}px` }} />
                </TopHorizontalScroll>
            )}
            <TopOnlyTableContainer ref={tableRef}>
                <Table type='default'>
                    <StickyTableHeader>
                        <tr>
                            <th className='sticky name' style={{ textAlign: 'left', paddingLeft: 20 }}>{l(991)}</th>
                            <th className='sticky season'>{l(123)}</th>
                            {tableHeadNames?.map((item, index) => {
                                const displayName = getParameterDisplayName(item);
                                return (
                                <ParameterHeader
                                    title={displayName}
                                    key={item.parameter_id || item.id}
                                    onClick={() => handleSortTable(item)}
                                    $highlighted={index === highlightedParameter?.index}
                                    $fading={Boolean(highlightedParameter?.isFading)}
                                >
                                    {extractFirstCharacters(displayName)}
                                    {
                                        tableTypeSorting.type === 'asc' && tableTypeSorting.param === (item.parameter_id || item.id) ? (
                                            <SortAscIcon />
                                        ) : tableTypeSorting.type === 'desc' && tableTypeSorting.param === (item.parameter_id || item.id) ? (
                                            <SortDescIcon />
                                        ) : (
                                            <SortDefaultIcon />
                                        )
                                    }
                                </ParameterHeader>
                                );
                            })}
                        </tr>
                    </StickyTableHeader>
                    <tbody>
                        {loading ? (
                            <tr>
                                <td colSpan={tableHeadNames?.length < 10 ? tableHeadNames?.length + 2 : tableHeadNames?.length > 10 ? 12 : 2}>
                                    <Oval
                                        visible={true}
                                        height="80"
                                        width="80"
                                        secondaryColor={
                                            localStorage.getItem("theme") === "light"
                                                ? "#4E5255"
                                                : "#CFDAE6"
                                        }
                                        color={
                                            localStorage.getItem("theme") === "light"
                                                ? "#2363F7"
                                                : "#4fa94d"
                                        }
                                        ariaLabel="oval-loading"
                                        wrapperStyle={{ height: '50vh', alignItems: 'center', justifyContent: 'center' }}
                                        wrapperClass=""
                                    />
                                </td>
                            </tr>
                        ) : data?.length > 0 ? (
                            <tr>
                                <td colSpan={colSpanCount} style={{ padding: 0 }}>
                                    <VirtualBody style={{ width: virtualTableWidth }}>
                                        <VariableSizeList
                                            ref={listRef}
                                            outerRef={listOuterRef}
                                            height={virtualListHeight}
                                            itemCount={virtualRows.length}
                                            itemSize={(index) =>
                                                virtualRows[index]?.type === 'subheader'
                                                    ? VIRTUAL_SUBHEADER_ROW_HEIGHT
                                                    : VIRTUAL_MATCH_ROW_HEIGHT
                                            }
                                            overscanCount={6}
                                            width={virtualTableWidth}
                                        >
                                            {renderVirtualRow}
                                        </VariableSizeList>
                                    </VirtualBody>
                                </td>
                            </tr>
                        ) : (
                            <tr>
                                <td colSpan={2}>
                                    <EmptyState style={{ height: '50vh', padding: 0 }}>
                                        <img src={emptySearch} alt={emptySearch} />
                                        <h3>{l(314)}</h3>
                                    </EmptyState>
                                </td>
                            </tr>
                        )
                        }
                    </tbody>
                </Table>
            </TopOnlyTableContainer>
        </>
    )
}
